import PropTypes from 'prop-types';
import React, { useState } from 'react';

import {
  Row,
  Col,
  Alert,
  Container,
  CardBody,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from 'reactstrap';

// Redux
import { withRouter, Link, useHistory } from 'react-router-dom';

// availity-reactstrap-validation
import { AvForm, AvField } from 'availity-reactstrap-validation';

// import images
import logo from '../../assets/images/dictionary.png';

import axios from 'axios';
import { baseUrl, setLanguageId } from '../../helpers/EncryptionHelper';

const Login = (props) => {
  const [error, setError] = useState();
  const [loginLoading, setLoginLoading] = useState(false);

  // Language modal state
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [languageError, setLanguageError] = useState('');
  const [fetchingLanguages, setFetchingLanguages] = useState(false);
  const [authData, setAuthData] = useState(null); // store login response temporarily

  const history = useHistory();

  /**
   * Step 1: Login with email/password only (no language selection needed).
   * On success, fetch languages using the token, then show the language modal.
   */
  const handleValidSubmit = (event, values) => {
    setError();
    setLoginLoading(true);

    axios
      .post(baseUrl + '/user/admin-login-email', {
        email: values?.email,
        password: values?.password,
      })
      .then((res) => {
        const token = res.data?.data?.jwtToken;

        // Set the token for subsequent requests
        axios.defaults.headers.common['token'] = token;
        axios.defaults.headers.common['Authorization'] = token;

        // Store login data temporarily (don't navigate yet)
        setAuthData(res.data.data);

        // Now fetch languages and admin users using the token
        setFetchingLanguages(true);

        const authHeaders = { headers: { Authorization: token } };

        Promise.all([
          axios.get(
            'https://api.theaflangproject.org/api/v1/mongo/language?isActive=true',
            authHeaders
          ),
          axios.get(
            'https://api.theaflangproject.org/api/v1/mongo/user/admin-all',
            authHeaders
          ),
        ])
          .then(([langsRes, adminsRes]) => {
            const fetchedLanguages =
              langsRes.data?.data?.languages || [];
            const fetchedAdminUsers =
              adminsRes.data?.data?.list || [];

            setLanguages(fetchedLanguages);
            setAdminUsers(fetchedAdminUsers);
            setFetchingLanguages(false);
            setLoginLoading(false);

            if (fetchedLanguages.length === 0) {
              setError({
                type: 'warning',
                text: 'No languages available. Please contact an administrator.',
              });
              return;
            }

            // Show the language selection modal
            setShowLanguageModal(true);
          })
          .catch((err) => {
            console.error('Failed to fetch languages/admin data', err);
            setFetchingLanguages(false);
            setLoginLoading(false);
            setError({
              type: 'warning',
              text: 'Login succeeded but failed to load languages. Please try again.',
            });
          });
      })
      .catch((error) => {
        console.log('====================================');
        console.log(error);
        console.log('====================================');
        setLoginLoading(false);
        setError({
          type: 'warning',
          text:
            error?.response?.data?.data?.message ||
            'Server Error, Please try again Later!',
        });
      });
  };

  /**
   * Step 2: User selects a language from the modal and we verify permissions.
   */
  const handleLanguageSelect = () => {
    setLanguageError('');

    if (!selectedLanguage) {
      setLanguageError('Please select a language to continue.');
      return;
    }

    const langObj = languages.find((l) => l._id === selectedLanguage);

    // Find the admin user from the stored auth data
    const userEmail = authData?.user?.email || authData?.email || '';
    const adminUser = adminUsers.find(
      (u) => u.email.toLowerCase() === userEmail.toLowerCase()
    );

    if (!adminUser) {
      setLanguageError('User not found or lacks permissions.');
      return;
    }

    const rawRole = adminUser.role || '';
    const trimmedRole = rawRole.trim();

    if (trimmedRole.toLowerCase() === 'user') {
      setLanguageError('Unauthorized access.');
      return;
    }

    if (trimmedRole.toLowerCase() !== 'all') {
      const languageName = langObj?.name || '';
      const constructedPermission = languageName.toLowerCase() + 'Word';

      if (!rawRole.includes(constructedPermission)) {
        setLanguageError(
          'You do not have permission to access this language.'
        );
        return;
      }
    }

    // All checks passed — finalize login
    sessionStorage.setItem('authUser', JSON.stringify(authData));
    setLanguageId(selectedLanguage);
    setShowLanguageModal(false);
    history.push('/dashboard');
  };

  /**
   * Cancel language selection — clear auth data, go back to login form.
   */
  const handleLanguageCancel = () => {
    setShowLanguageModal(false);
    setAuthData(null);
    setSelectedLanguage('');
    setLanguageError('');
    // Clear the token since the user cancelled
    delete axios.defaults.headers.common['token'];
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <React.Fragment>
      <div className='account-pages my-5 pt-sm-5'>
        <Container>
          <Row>
            <Col lg={12}>
              <div className='text-center'>
                <Link
                  to='/'
                  className='mb-5 d-block auth-logo justify-content-center align-items-center'
                >
                  <img
                    src={logo}
                    alt=''
                    height='48'
                    className='logo logo-dark'
                  />
                  <h3 className='mt-1'>Admin</h3>
                </Link>
              </div>
            </Col>
          </Row>
          <Row className='align-items-center justify-content-center'>
            <Col md={8} lg={6} xl={5}>
              <Card>
                <CardBody className='p-4'>
                  <div className='text-center mt-2'>
                    <h5 className='text-primary'>Welcome Back !</h5>
                    <p className='text-muted'>
                      Sign in to continue to Dictionary Admin.
                    </p>
                  </div>
                  <div className='p-2 mt-4'>
                    <AvForm
                      className='form-horizontal'
                      onValidSubmit={(e, v) => {
                        handleValidSubmit(e, v);
                      }}
                    >
                      {props.error && typeof props.error === 'string' ? (
                        <Alert color='danger'>{props.error}</Alert>
                      ) : null}

                      <div className='mb-3'>
                        <AvField
                          name='email'
                          label='Email'
                          value=''
                          className='form-control'
                          placeholder='Enter email'
                          type='email'
                          required
                          onChange={() => setError()}
                        />
                      </div>

                      <div className='mb-3'>
                        <div className='float-end'></div>
                        <AvField
                          name='password'
                          label='Password'
                          value=''
                          type='password'
                          required
                          placeholder='Enter Password'
                          onChange={() => setError()}
                        />
                      </div>

                      {error && (
                        <Alert color={error?.type || 'danger'} role='alert'>
                          {error?.text || 'ERROR!'}
                        </Alert>
                      )}

                      <div className='mt-4'>
                        <button
                          className='btn btn-primary w-100 waves-effect waves-light'
                          type='submit'
                          disabled={loginLoading}
                        >
                          {loginLoading ? (
                            <>
                              <Spinner size='sm' className='me-2' />
                              Signing in...
                            </>
                          ) : (
                            'Log In'
                          )}
                        </button>
                      </div>
                    </AvForm>
                  </div>
                </CardBody>
              </Card>
              <div className='mt-5 text-center'>
                <p>
                  © {new Date().getFullYear()} Dictionary. Crafted with{' '}
                  <i className='mdi mdi-heart text-danger'></i> in India
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Language Selection Modal — shown after successful login */}
      <Modal
        isOpen={showLanguageModal}
        backdrop='static'
        keyboard={false}
        centered
      >
        <ModalHeader tag='h5' className='text-primary'>
          Select Language
        </ModalHeader>
        <ModalBody>
          {fetchingLanguages ? (
            <div className='text-center py-4'>
              <Spinner color='primary' />
              <p className='mt-2 text-muted'>Loading languages...</p>
            </div>
          ) : (
            <>
              <p className='text-muted mb-3'>
                Please select a language to continue to the dashboard.
              </p>
              <div className='mb-3'>
                <label htmlFor='languageSelect' className='form-label'>
                  Language
                </label>
                <select
                  id='languageSelect'
                  className='form-control form-select'
                  value={selectedLanguage}
                  onChange={(e) => {
                    setSelectedLanguage(e.target.value);
                    setLanguageError('');
                  }}
                >
                  <option value='' disabled>
                    Select a language
                  </option>
                  {languages.map((lang) => (
                    <option key={lang._id} value={lang._id}>
                      {lang.name}
                    </option>
                  ))}
                </select>
              </div>
              {languageError && (
                <Alert color='danger' role='alert'>
                  {languageError}
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        {!fetchingLanguages && (
          <ModalFooter>
            <button
              className='btn btn-secondary'
              onClick={handleLanguageCancel}
            >
              Cancel
            </button>
            <button
              className='btn btn-primary'
              onClick={handleLanguageSelect}
              disabled={!selectedLanguage}
            >
              Continue
            </button>
          </ModalFooter>
        )}
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(Login);

Login.propTypes = {
  error: PropTypes.any,
  history: PropTypes.object,
  loginUser: PropTypes.func,
  socialLogin: PropTypes.func,
};
